/**
 * `hreflang` alternate-set builder, shared across the whole marketing SEO surface
 * (GA-13 / ADR-0133). The marketing site publishes every indexable route in all
 * six locales (English unprefixed = `x-default`, others under `/<locale>/…`), so
 * each page emits one `<link rel="alternate" hreflang>` per locale plus an
 * `x-default` pointing at the English version.
 *
 * Pure (no React, no `window`) so the SSR entry, the prerender, and components
 * can all import it. Lifted out of `pages/docs/doc-links.ts` (docs-only origin,
 * ADR-0075) now that the alternate set is site-wide; that module re-exports this
 * for back-compat.
 */
import { SUPPORTED_LOCALES } from '@syncanix/shared';

import { localizedPath } from '@/i18n/locale-path';

import type { AlternateLink } from './page-meta';

/**
 * Build the `hreflang` alternate set for an English-relative path: one entry per
 * supported locale plus an `x-default` pointing at the English (unprefixed)
 * version. The input is always the **English-relative** path (e.g. `/pricing`,
 * `/docs/introduction`); pass it without a locale prefix.
 */
export function localeAlternates(enRelativePath: string): AlternateLink[] {
  const links: AlternateLink[] = SUPPORTED_LOCALES.map((l) => ({
    hreflang: l.code,
    href: localizedPath(enRelativePath, l.code),
  }));
  links.push({ hreflang: 'x-default', href: localizedPath(enRelativePath, 'en') });
  return links;
}
